/* eslint-disable new-cap */
/* eslint-disable operator-assignment */
/* eslint-disable prefer-const */

import React, { useEffect, useState } from "react";
// @mui
import { styled } from "@mui/material/styles";
import {
  Box,
  Card,
  Grid,
  Table,
  Divider,
  TableRow,
  TableBody,
  TableHead,
  TableCell,
  Typography,
  TableContainer,
  Container,
  Button,
  LinearProgress,
} from "@mui/material";
import { useQuery, useQueryClient } from "react-query";
import { useLocation, useNavigate } from "react-router-dom";
import html2canvas from "html2canvas";
import jsPDF from "jspdf";
// components
import Image from "../../components/Image";
import Scrollbar from "../../components/Scrollbar";
import { invoiceGet } from "../api";
import Iconify from "../../components/Iconify";
import { fDate } from "../../utils/formatTime";
import Page from "src/components/Page";
import HeaderBreadcrumbs from "src/components/HeaderBreadcrumbs";
import useSettings from "src/hooks/useSettings";
import { PATH_DASHBOARD } from "src/routes/paths";
import Logo from "../../assets/logo.png";
import { HOST_API } from "src/config-prod";

const RowResultStyle = styled(TableRow)(({ theme }) => ({
  "& td": {
    paddingTop: theme.spacing(1),
    paddingBottom: theme.spacing(1),
  },
}));

const headerStyle = {
  border: "2px solid black",
  background: "rgb(192, 192, 192)",
  color: "black",
  fontSize: "14px",
};

const cellStyle = {
  border: "2px solid black",
  fontSize: "13px",
};

export function groupBy(array, key) {
  return array.reduce((result, currentItem) => {
    const group = currentItem[key];
    if (!result[group]) {
      result[group] = [];
    }
    result[group].push(currentItem);
    return result;
  }, {});
}

// ----------------------------------------------------------------------
export default function InvoiceDetails() {
  const { themeStretch } = useSettings();
  const queryClient = useQueryClient();
  const { state } = useLocation();
  const navigate = useNavigate();
  const [invoiceData, setInvoiceData] = useState(false);
  const [isDownloading, setIsDownloading] = useState(false);

  useEffect(() => {
    if (state === null) {
      navigate("/dashboard/app");
    }
  }, [state]);

  const { data, isLoading, isError } = useQuery({
    queryKey: ["invoiceGet", state?.orderID, state?.vendorId],
    queryFn: ({ queryKey }) =>
      invoiceGet({ orderID: queryKey[1], vendorId: queryKey[2] }),
    enabled: !!state?.orderID && !!state?.vendorId,
  });

  const handleTryAgain = () => {
    queryClient.invalidateQueries("invoiceGet");
  };

  useEffect(() => {
    const groupedData = groupBy(data?.orderDetails || [], "catName");
    setInvoiceData(groupedData);
  }, [data]);

  const isMaharashtra = data?.studentData?.state === 27;
  const totalColumns = isMaharashtra ? 8 : 7;
  const colSpanBeforeTaxColumn = totalColumns - 2;

  // ─── PDF DOWNLOAD ─────────────────────────────────────────────────────────
  const handleDownloadPDF = async () => {
    setIsDownloading(true);
    try {
      const pdf = new jsPDF("p", "mm", "a4");
      const pdfWidth = pdf.internal.pageSize.getWidth();

      const invoicePages = document.querySelectorAll(".pdf-invoice-page");

      if (invoicePages.length === 0) {
        alert("No invoice data to generate PDF");
        setIsDownloading(false);
        return;
      }

      for (let i = 0; i < invoicePages.length; i++) {
        const page = invoicePages[i];
        const canvas = await html2canvas(page, {
          scale: 2,
          useCORS: true,
          logging: false,
          allowTaint: false,
          backgroundColor: "#ffffff",
        });

        const imgData = canvas.toDataURL("image/jpeg", 0.95);
        const imgWidth = pdfWidth - 10;
        const imgHeight = (canvas.height * imgWidth) / canvas.width;

        if (i > 0) {
          pdf.addPage();
        }

        pdf.addImage(imgData, "JPEG", 5, 5, imgWidth, imgHeight);
      }

      pdf.save(
        `invoice-Order#${data?.studentData?.orderID || state?.orderID}.pdf`
      );
    } catch (error) {
      console.error("PDF generation error:", error);
      alert("PDF generation failed. Please try again.");
    } finally {
      setIsDownloading(false);
    }
  };

  // ─── PDF HEADER ───────────────────────────────────────────────────────────
  const PDFInvoiceHeader = () => (
    <>
      <Grid container>
        <Grid item xs={12} sm={6} sx={{ mb: 3 }}>
          <Image
            disabledEffect
            visibleByDefault
            alt="company-logo"
            src={
              data?.vendorDetails?.image
                ? `${HOST_API}images/product/${data.vendorDetails.image}`
                : Logo
            }
            sx={{ maxWidth: 120 }}
          />
        </Grid>
        <Grid item xs={12} sm={6} sx={{ mb: 3 }}>
          <Box sx={{ textAlign: { sm: "right" } }}>
            <Typography variant="h6" sx={{ fontSize: "18px" }}>
              OrderId #{data?.studentData?.orderID || state?.orderID}
            </Typography>
          </Box>
        </Grid>
        <Grid item xs={12} sm={12} sx={{ mb: 3 }}>
          <Typography variant="h5" fontWeight="bold" sx={{ fontSize: "20px" }}>
            Sold By:{" "}
            {data?.vendorDetails?.companyName
              ? data.vendorDetails.companyName
                  .toLowerCase()
                  .replace(/\b\w/g, (c) => c.toUpperCase())
              : "PUPIL KART"}
          </Typography>
          <Typography variant="subtitle1" sx={{ fontSize: "14px" }}>
            GSTIN: {data?.vendorDetails?.gst || "27AAMFB1315K1ZI"}
          </Typography>
        </Grid>
        <Grid item xs={2} sx={{ fontSize: "14px" }}>
          Retail / Tax
        </Grid>
        <Grid item xs={4}>
          <Typography sx={{ fontSize: "14px" }}>
            <b>Order :</b> #{data?.studentData?.orderID || state?.orderID}
          </Typography>
        </Grid>
        <Grid item xs={4}>
          <Typography sx={{ fontSize: "14px" }}>
            <b>Invoice :</b> #{data?.studentData?.invoiceNo || state?.invoiceNo}
          </Typography>
        </Grid>
        <Grid item xs={2}>
          <Typography sx={{ fontSize: "14px" }}>
            <b>Payment Mode</b>
          </Typography>
        </Grid>
        <Grid item xs={2} sx={{ fontSize: "14px" }}>
          Invoice / Bill
        </Grid>
        <Grid item xs={4}>
          <Typography sx={{ fontSize: "14px" }}>
            <b>Order Date :</b>{" "}
            {(data?.studentData?.paymentDate &&
              fDate(data?.studentData?.paymentDate)) ||
              "-"}
          </Typography>
        </Grid>
        <Grid item xs={4}></Grid>
        <Grid item xs={2}>
          <Typography sx={{ fontSize: "14px" }}>
            {data?.studentData?.paymentMode
              ? data.studentData.paymentMode.toUpperCase()
              : "-"}
          </Typography>
        </Grid>
      </Grid>
      <Box
        sx={{
          mt: 1,
          background: "rgb(192, 192, 192)",
          color: "black",
          padding: "10px",
          display: "flex",
          flexWrap: "wrap",
          alignItems: "center",
        }}
      >
        <Typography
          sx={{
            width: { xs: "100%", md: "50%" },
            fontSize: "14px",
            fontWeight: "bold",
          }}
        >
          Shipping / Billing Details:
        </Typography>
        <Typography
          sx={{
            width: { xs: "100%", md: "50%" },
            fontSize: "14px",
            fontWeight: "bold",
          }}
        >
          Student Details:
        </Typography>
      </Box>
      <Grid container spacing={0} mb={2}>
        <Grid item xs={12} md={6}>
          <Typography sx={{ fontSize: "14px" }}>
            <b>
              {data?.studentData?.parentFirstName || ""}{" "}
              {data?.studentData?.parentLastName || ""}
            </b>
          </Typography>
          <Typography
            sx={{
              whiteSpace: "pre-wrap",
              wordBreak: "break-word",
              maxWidth: "100%",
              fontSize: "14px",
            }}
          >
            {data?.studentData?.address1} {data?.studentData?.address2}{" "}
            {data?.studentData?.city}, {data?.studentData?.stateName}{" "}
            {data?.studentData?.pin}.
          </Typography>
          <Typography sx={{ fontSize: "14px" }}>
            <b>Mobile No:</b> +91 {data?.studentData?.mobile}
          </Typography>
        </Grid>
        <Grid item xs={12} md={6}>
          <Typography sx={{ fontSize: "14px" }}>
            <b>
              {data?.studentData?.firstName} {data?.studentData?.lastName}
            </b>
          </Typography>
          <Typography sx={{ fontSize: "14px" }}>
            <b>School:</b> {data?.studentData?.schoolName || "-"}
          </Typography>
          <Typography sx={{ fontSize: "14px" }}>
            <b>Grade:</b> {data?.studentData?.stdName || "-"}
          </Typography>
          <Typography sx={{ fontSize: "14px" }}>
            <b>Division:</b> {data?.studentData?.division || "-"}
          </Typography>
          <Typography sx={{ fontSize: "14px" }}>
            <b>Roll No:</b> {data?.studentData?.rollNo || "-"}
          </Typography>
        </Grid>
      </Grid>
    </>
  );

  // ─── RENDER PDF TABLE PER SLAB ────────────────────────────────────────────
  // Each slab (without-GST / per-GST-rate) = separate invoice.
  // Sr. No. always starts from 1 within each slab invoice.
  const renderPDFTable = (items, title) => {
    const grouped = groupBy(items, "catName");
    const subtotal = items.reduce(
      (acc, crr) => acc + crr.totalPriceBeforeTax,
      0
    );
    const taxes = items.reduce(
      (acc, crr) => acc + (crr.amount - crr.totalPriceBeforeTax),
      0
    );
    const total = items.reduce((acc, crr) => acc + crr.amount, 0);

    // Flatten items preserving category info
    const allItems = Object.entries(grouped).flatMap(([category, catItems]) =>
      catItems.map((item) => ({ ...item, category }))
    );

    const firstPageMax = 6;
    const subsequentPageMax = 13;
    const totalsReserveSlots = 3;

    const pages = [];
    if (allItems.length > 0) {
      let start = 0;
      let pg = 0;

      while (start < allItems.length) {
        const capacity = pg === 0 ? firstPageMax : subsequentPageMax;
        const remaining = allItems.length - start;
        const willBeLastPage = remaining <= capacity;

        if (willBeLastPage) {
          const safeCapacity = capacity - totalsReserveSlots;
          if (remaining > safeCapacity) {
            pages.push(allItems.slice(start, start + safeCapacity));
            start += safeCapacity;
          } else {
            pages.push(allItems.slice(start, start + remaining));
            start += remaining;
          }
        } else {
          pages.push(allItems.slice(start, start + capacity));
          start += capacity;
        }
        pg++;
      }
    }

    // Pre-compute start index for each page (Sr. No. restarts from 1 per slab)
    const pageStartIndices = pages.reduce((acc, pageItems, i) => {
      acc.push(i === 0 ? 0 : acc[i - 1] + pages[i - 1].length);
      return acc;
    }, []);

    const pageElements = [];

    pages.forEach((pageItems, pageIndex) => {
      const isFirstPage = pageIndex === 0;
      const isLastPage = pageIndex === pages.length - 1;
      const showTotals = isLastPage;
      const pageStartIndex = pageStartIndices[pageIndex];

      pageElements.push(
        <Box
          key={`table-page-${title}-${pageIndex}`}
          className="pdf-invoice-page"
          sx={{
            width: "210mm",
            height: "297mm",
            padding: "10mm 15mm",
            backgroundColor: "white",
            boxSizing: "border-box",
            display: "block",
            pageBreakAfter: "always",
            pageBreakBefore: "auto",
            overflow: "hidden",
          }}
        >
          {/* Header only on first page of each slab */}
          {isFirstPage && <PDFInvoiceHeader />}

          {/* Title bar — first page only */}
          {isFirstPage && (
            <Typography
              variant="h6"
              sx={{
                mb: 2,
                mt: 1,
                background: "rgb(192, 192, 192)",
                color: "black",
                padding: "10px",
                borderRadius: "4px",
                textAlign: "center",
                fontSize: "15px",
                fontWeight: "bold",
              }}
            >
              {title}
            </Typography>
          )}

          {!isFirstPage && <Box sx={{ mt: 4 }} />}

          <TableContainer>
            <Table
              sx={{
                width: "100%",
                border: "2px solid black",
                tableLayout: "fixed",
                borderCollapse: "collapse",
              }}
            >
              <TableHead>
                <TableRow>
                  <TableCell
                    align="center"
                    sx={{
                      ...headerStyle,
                      width: "5%",
                      visibility: isFirstPage ? "visible" : "hidden",
                      height: isFirstPage ? "auto" : "0px",
                      padding: isFirstPage ? "6px" : "0px",
                      border: isFirstPage ? "2px solid black" : "none",
                      lineHeight: isFirstPage ? "normal" : "0",
                    }}
                  >
                    {isFirstPage && "Sr. No."}
                  </TableCell>
                  <TableCell
                    align="left"
                    sx={{
                      ...headerStyle,
                      width: "25%",
                      visibility: isFirstPage ? "visible" : "hidden",
                      height: isFirstPage ? "auto" : "0px",
                      padding: isFirstPage ? "6px" : "0px",
                      border: isFirstPage ? "2px solid black" : "none",
                      lineHeight: isFirstPage ? "normal" : "0",
                    }}
                  >
                    {isFirstPage && "Items"}
                  </TableCell>
                  <TableCell
                    align="center"
                    sx={{
                      ...headerStyle,
                      width: "15%",
                      visibility: isFirstPage ? "visible" : "hidden",
                      height: isFirstPage ? "auto" : "0px",
                      padding: isFirstPage ? "6px" : "0px",
                      border: isFirstPage ? "2px solid black" : "none",
                      lineHeight: isFirstPage ? "normal" : "0",
                    }}
                  >
                    {isFirstPage && "HSN Code"}
                  </TableCell>
                  <TableCell
                    align="center"
                    sx={{
                      ...headerStyle,
                      width: "6%",
                      visibility: isFirstPage ? "visible" : "hidden",
                      height: isFirstPage ? "auto" : "0px",
                      padding: isFirstPage ? "6px" : "0px",
                      border: isFirstPage ? "2px solid black" : "none",
                      lineHeight: isFirstPage ? "normal" : "0",
                    }}
                  >
                    {isFirstPage && "Qty"}
                  </TableCell>
                  <TableCell
                    align="center"
                    sx={{
                      ...headerStyle,
                      width: "12%",
                      visibility: isFirstPage ? "visible" : "hidden",
                      height: isFirstPage ? "auto" : "0px",
                      padding: isFirstPage ? "6px" : "0px",
                      border: isFirstPage ? "2px solid black" : "none",
                      lineHeight: isFirstPage ? "normal" : "0",
                    }}
                  >
                    {isFirstPage && "Total Price"}
                  </TableCell>
                  {isMaharashtra ? (
                    <>
                      <TableCell
                        align="center"
                        sx={{
                          ...headerStyle,
                          width: "10%",
                          visibility: isFirstPage ? "visible" : "hidden",
                          height: isFirstPage ? "auto" : "0px",
                          padding: isFirstPage ? "6px" : "0px",
                          border: isFirstPage ? "2px solid black" : "none",
                          lineHeight: isFirstPage ? "normal" : "0",
                        }}
                      >
                        {isFirstPage && "CGST"}
                      </TableCell>
                      <TableCell
                        align="center"
                        sx={{
                          ...headerStyle,
                          width: "10%",
                          visibility: isFirstPage ? "visible" : "hidden",
                          height: isFirstPage ? "auto" : "0px",
                          padding: isFirstPage ? "6px" : "0px",
                          border: isFirstPage ? "2px solid black" : "none",
                          lineHeight: isFirstPage ? "normal" : "0",
                        }}
                      >
                        {isFirstPage && "SGST"}
                      </TableCell>
                    </>
                  ) : (
                    <TableCell
                      align="center"
                      sx={{
                        ...headerStyle,
                        width: "10%",
                        visibility: isFirstPage ? "visible" : "hidden",
                        height: isFirstPage ? "auto" : "0px",
                        padding: isFirstPage ? "6px" : "0px",
                        border: isFirstPage ? "2px solid black" : "none",
                        lineHeight: isFirstPage ? "normal" : "0",
                      }}
                    >
                      {isFirstPage && "IGST"}
                    </TableCell>
                  )}
                  <TableCell
                    align="center"
                    sx={{
                      ...headerStyle,
                      width: "12%",
                      visibility: isFirstPage ? "visible" : "hidden",
                      height: isFirstPage ? "auto" : "0px",
                      padding: isFirstPage ? "6px" : "0px",
                      border: isFirstPage ? "2px solid black" : "none",
                      lineHeight: isFirstPage ? "normal" : "0",
                    }}
                  >
                    {isFirstPage && "Total"}
                  </TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {pageItems.map((el, index) => {
                  // Sr. No. starts from 1 within each slab invoice
                  const srNo = pageStartIndex + index + 1;

                  // Show category header when category changes OR at start of each page
                  const showCategoryHeader =
                    index === 0 || pageItems[index - 1]?.category !== el.category;

                  return (
                    <React.Fragment key={`item-${pageIndex}-${index}`}>
                      {showCategoryHeader && (
                        <TableRow>
                          <TableCell
                            colSpan={totalColumns}
                            align="center"
                            sx={{
                              border: "2px solid black",
                              background: "rgb(240, 240, 240)",
                              fontWeight: "bold",
                              fontSize: "13px",
                              padding: "6px",
                              textAlign: "center",
                            }}
                          >
                            {el.category}
                          </TableCell>
                        </TableRow>
                      )}

                      <TableRow>
                        <TableCell
                          align="center"
                          sx={{
                            ...cellStyle,
                            padding: "8px 4px",
                            fontSize: "11px",
                            height: "auto",
                            width: "5%",
                          }}
                        >
                          {srNo}
                        </TableCell>
                        <TableCell
                          align="left"
                          sx={{
                            ...cellStyle,
                            padding: "8px 4px",
                            height: "auto",
                            width: "25%",
                          }}
                        >
                          <Typography
                            sx={{
                              fontSize: "11px",
                              fontWeight: "bold",
                              marginBottom: "2px",
                              lineHeight: "1.2",
                            }}
                          >
                            {el.prodName}
                          </Typography>
                          <Typography
                            sx={{
                              fontStyle: "italic",
                              fontSize: "9px",
                              lineHeight: "1.2",
                              color: "#555",
                              minHeight: "12px",
                            }}
                          >
                            {el.prodDesc &&
                            el.prodDesc.trim() !== "" &&
                            el.prodDesc !== "null"
                              ? el.prodDesc
                              : "No description available"}
                          </Typography>
                        </TableCell>
                        <TableCell
                          align="center"
                          sx={{
                            ...cellStyle,
                            padding: "8px 4px",
                            fontSize: "10px",
                            height: "auto",
                            width: "10%",
                          }}
                        >
                          {el?.hsncode || "-"}
                        </TableCell>
                        <TableCell
                          align="center"
                          sx={{
                            ...cellStyle,
                            padding: "8px 4px",
                            fontSize: "11px",
                            height: "auto",
                            width: "6%",
                          }}
                        >
                          {el.qty}
                        </TableCell>
                        <TableCell
                          align="center"
                          sx={{
                            ...cellStyle,
                            padding: "8px 4px",
                            fontSize: "10px",
                            height: "auto",
                            width: "12%",
                          }}
                        >
                          ₹ {el?.totalPriceBeforeTax?.toFixed(2) || "0.00"}
                        </TableCell>
                        {isMaharashtra ? (
                          <>
                            <TableCell
                              align="center"
                              sx={{
                                ...cellStyle,
                                padding: "8px 4px",
                                fontSize: "10px",
                                lineHeight: "1.2",
                                height: "auto",
                                width: "10%",
                              }}
                            >
                              {el.cgst}%<br />
                              <span style={{ fontSize: "9px" }}>
                                ₹ {el?.cgstAmount?.toFixed(2) || "0.00"}
                              </span>
                            </TableCell>
                            <TableCell
                              align="center"
                              sx={{
                                ...cellStyle,
                                padding: "8px 4px",
                                fontSize: "10px",
                                lineHeight: "1.2",
                                height: "auto",
                                width: "10%",
                              }}
                            >
                              {el.sgst}%<br />
                              <span style={{ fontSize: "9px" }}>
                                ₹ {el?.sgstAmount?.toFixed(2) || "0.00"}
                              </span>
                            </TableCell>
                          </>
                        ) : (
                          <TableCell
                            align="center"
                            sx={{
                              ...cellStyle,
                              padding: "8px 4px",
                              fontSize: "10px",
                              lineHeight: "1.2",
                              height: "auto",
                              width: "10%",
                            }}
                          >
                            {el.igst}%<br />
                            <span style={{ fontSize: "9px" }}>
                              ₹ {el?.igstAmount?.toFixed(2) || "0.00"}
                            </span>
                          </TableCell>
                        )}
                        <TableCell
                          align="center"
                          sx={{
                            ...cellStyle,
                            padding: "8px 4px",
                            fontSize: "10px",
                            fontWeight: "bold",
                            height: "auto",
                            width: "12%",
                          }}
                        >
                          ₹ {el?.amount?.toFixed(2) || "0.00"}
                        </TableCell>
                      </TableRow>
                    </React.Fragment>
                  );
                })}

                {/* Totals — only on last page of this slab */}
                {showTotals && (
                  <>
                    <RowResultStyle>
                      <TableCell
                        sx={{ ...cellStyle, padding: "8px" }}
                        colSpan={colSpanBeforeTaxColumn}
                      />
                      <TableCell
                        align="right"
                        sx={{ ...cellStyle, fontWeight: "bold", padding: "8px", fontSize: "11px" }}
                      >
                        Subtotal:
                      </TableCell>
                      <TableCell
                        align="right"
                        sx={{ ...cellStyle, fontWeight: "bold", padding: "8px", fontSize: "11px" }}
                      >
                        ₹ {Number(subtotal).toFixed(2)}
                      </TableCell>
                    </RowResultStyle>
                    <RowResultStyle>
                      <TableCell
                        sx={{ ...cellStyle, padding: "8px" }}
                        colSpan={colSpanBeforeTaxColumn}
                      />
                      <TableCell
                        align="right"
                        sx={{ ...cellStyle, fontWeight: "bold", padding: "8px", fontSize: "11px" }}
                      >
                        Taxes:
                      </TableCell>
                      <TableCell
                        align="right"
                        sx={{ ...cellStyle, fontWeight: "bold", padding: "8px", fontSize: "11px" }}
                      >
                        ₹ {Number(taxes).toFixed(2)}
                      </TableCell>
                    </RowResultStyle>
                    <RowResultStyle>
                      <TableCell
                        sx={{ ...cellStyle, padding: "8px" }}
                        colSpan={colSpanBeforeTaxColumn}
                      />
                      <TableCell
                        align="right"
                        sx={{
                          ...cellStyle,
                          fontWeight: "bold",
                          background: "rgb(240, 240, 240)",
                          padding: "8px",
                          fontSize: "12px",
                        }}
                      >
                        Total:
                      </TableCell>
                      <TableCell
                        align="right"
                        sx={{
                          ...cellStyle,
                          fontWeight: "bold",
                          background: "rgb(240, 240, 240)",
                          padding: "8px",
                          fontSize: "12px",
                        }}
                      >
                        ₹ {Number(total).toFixed(2)}
                      </TableCell>
                    </RowResultStyle>
                  </>
                )}
              </TableBody>
            </Table>
          </TableContainer>

          {showTotals && (
            <Box sx={{ mt: 3 }}>
              <Divider sx={{ mb: 1 }} />
              <Grid container>
                <Grid item xs={12} md={9} sx={{ py: 1 }}>
                  <Typography
                    variant="subtitle2"
                    sx={{ fontSize: "11px", fontWeight: "bold" }}
                  >
                    Declaration
                  </Typography>
                  <Typography variant="body2" sx={{ fontSize: "10px" }}>
                    The goods sold are intended for end user consumption and not
                    for resale.
                    <br />
                    This is a computer generated invoice.
                  </Typography>
                </Grid>
                <Grid item xs={12} md={3} sx={{ py: 1, textAlign: "right" }}>
                  <Typography
                    variant="subtitle2"
                    sx={{ fontSize: "11px", fontWeight: "bold" }}
                  >
                    Order Through
                  </Typography>
                </Grid>
              </Grid>
            </Box>
          )}
        </Box>
      );
    });

    return pageElements;
  };

  // ─── SHIPPING PAGE ────────────────────────────────────────────────────────
  const renderShippingPDFPage = () => {
    const shippingPrice = Number(data?.studentData?.shippingPrice || 0);
    const discountAmount = Number(data?.studentData?.discountAmount || 0);
    const shippingTotal = shippingPrice - discountAmount;
    const packageName = data?.orderDetails?.[0]?.packageName || "NA";

    return (
      <Box
        className="pdf-invoice-page"
        sx={{
          width: "210mm",
          height: "297mm",
          padding: "10mm 15mm",
          backgroundColor: "white",
          boxSizing: "border-box",
          display: "block",
          overflow: "hidden",
        }}
      >
        <PDFInvoiceHeader />

        <Typography
          variant="h6"
          sx={{
            mb: 2,
            mt: 1,
            background: "rgb(192, 192, 192)",
            color: "black",
            padding: "10px",
            borderRadius: "4px",
            textAlign: "center",
            fontSize: "15px",
            fontWeight: "bold",
          }}
        >
          Shipping Details: {packageName}
        </Typography>

        <TableContainer>
          <Table
            sx={{
              width: "100%",
              border: "2px solid black",
              tableLayout: "fixed",
              borderCollapse: "collapse",
            }}
          >
            <TableHead>
              <TableRow>
                <TableCell align="center" sx={{ ...headerStyle }}>Shipping Amount</TableCell>
                <TableCell align="center" sx={{ ...headerStyle }}>Discount Amount</TableCell>
                <TableCell align="center" sx={{ ...headerStyle }}>Total</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              <TableRow>
                <TableCell align="center" sx={{ ...cellStyle, fontSize: "12px", padding: "10px" }}>
                  ₹ {shippingPrice.toFixed(2)}
                </TableCell>
                <TableCell
                  align="center"
                  sx={{
                    ...cellStyle,
                    fontSize: "12px",
                    padding: "10px",
                    color: discountAmount > 0 ? "green" : "inherit",
                  }}
                >
                  {discountAmount > 0 ? `-₹ ${discountAmount.toFixed(2)}` : "₹ 0.00"}
                </TableCell>
                <TableCell
                  align="center"
                  sx={{
                    ...cellStyle,
                    fontSize: "12px",
                    fontWeight: "bold",
                    padding: "10px",
                    background: "rgb(240,240,240)",
                  }}
                >
                  ₹ {shippingTotal.toFixed(2)}
                </TableCell>
              </TableRow>
            </TableBody>
          </Table>
        </TableContainer>

        <Box
          sx={{
            mt: 2,
            px: 1.5,
            py: 1,
            border: "1px solid #f0a500",
            borderRadius: "6px",
            background: "#fffbea",
            display: "flex",
            alignItems: "flex-start",
            gap: "8px",
          }}
        >
          <Typography sx={{ fontSize: "13px", fontWeight: "bold", color: "#b07800", mt: "1px", flexShrink: 0 }}>
            Note:
          </Typography>
          <Typography sx={{ fontSize: "12px", color: "#7a5500", lineHeight: 1.6, fontStyle: "italic" }}>
            The shipping amount mentioned above is inclusive of GST. No separate GST is charged on the shipping amount.
          </Typography>
        </Box>

        <Box sx={{ mt: 3 }}>
          <Divider sx={{ mb: 1 }} />
          <Grid container>
            <Grid item xs={12} md={9} sx={{ py: 1 }}>
              <Typography variant="subtitle2" sx={{ fontSize: "11px", fontWeight: "bold" }}>
                Declaration
              </Typography>
              <Typography variant="body2" sx={{ fontSize: "10px" }}>
                The goods sold are intended for end user consumption and not for resale.
                <br />
                This is a computer generated invoice.
              </Typography>
            </Grid>
            <Grid item xs={12} md={3} sx={{ py: 1, textAlign: "right" }}>
              <Typography variant="subtitle2" sx={{ fontSize: "11px", fontWeight: "bold" }}>
                Order Through
              </Typography>
            </Grid>
          </Grid>
        </Box>
      </Box>
    );
  };

  if (isLoading) {
    return (
      <Page title="Master">
        <Container maxWidth={themeStretch ? false : "xl"}>
          <HeaderBreadcrumbs
            heading="Invoice"
            links={[
              { name: "Dashboard", href: PATH_DASHBOARD.root },
              { name: "Order-Detail" },
              { name: "Invoice" },
            ]}
          />
          <LinearProgress />
        </Container>
      </Page>
    );
  }

  return (
    <>
      <Page title="Master">
        <Container maxWidth={themeStretch ? false : "xl"}>
          <HeaderBreadcrumbs
            heading="Invoice"
            links={[
              { name: "Dashboard", href: PATH_DASHBOARD.root },
              { name: "Order-Detail" },
              { name: "Invoice" },
            ]}
          />
          <Button
            variant="outlined"
            color="inherit"
            startIcon={<Iconify icon={"eva:arrow-ios-back-fill"} width={20} height={20} />}
            sx={{ mb: 2 }}
            onClick={() => navigate("/dashboard/app/")}
          >
            Back to Orders
          </Button>
          <Button
            color="inherit"
            variant="outlined"
            startIcon={<Iconify icon={"material-symbols:download-rounded"} />}
            sx={{ mb: 2, ml: 2 }}
            onClick={handleDownloadPDF}
            disabled={isError || isDownloading}
          >
            {isDownloading ? "Generating PDF..." : "Download Invoice"}
          </Button>

          {isError && (
            <Typography variant="h5">
              Something Went Wrong Please{" "}
              <Button variant="text" onClick={handleTryAgain}>
                try again
              </Button>
            </Typography>
          )}

          {!isError && (
            <>
              {/* ── UI CARD — continuous Sr. No. across all categories ── */}
              <Card
                sx={{ pt: 5, px: 5, mb: 5, transform: "scale(0.98)", overflowX: "auto" }}
                id="invoice-container"
              >
                <Grid container>
                  <Grid item xs={12} sm={6} sx={{ mb: 5 }}>
                    <Image
                      disabledEffect
                      visibleByDefault
                      alt="logo"
                      src={
                        data?.vendorDetails?.image
                          ? `${HOST_API}images/product/${data.vendorDetails.image}`
                          : Logo
                      }
                      sx={{ maxWidth: 120 }}
                    />
                  </Grid>
                  <Grid item xs={12} sm={6} sx={{ mb: 5 }}>
                    <Box sx={{ textAlign: { sm: "right" } }}>
                      <Typography variant="h6">
                        OrderId #{data?.studentData?.orderID || state?.orderID}
                      </Typography>
                    </Box>
                  </Grid>
                  <Grid item xs={12} sm={12} sx={{ mb: 5 }}>
                    <Typography variant="h5" fontWeight="bold">
                      Sold By:{" "}
                      {data?.vendorDetails?.companyName
                        ? data.vendorDetails.companyName
                            .toLowerCase()
                            .replace(/\b\w/g, (char) => char.toUpperCase())
                        : "PUPIL KART"}
                    </Typography>
                    <Typography variant="subtitle1">
                      GSTIN: {data?.vendorDetails?.gst || "27AAMFB1315K1ZI"}
                    </Typography>
                  </Grid>

                  <Grid item xs={2}>Retail / Tax</Grid>
                  <Grid item xs={4}>
                    <Typography><b>Order: </b>#{data?.studentData?.orderID || state?.orderID}</Typography>
                  </Grid>
                  <Grid item xs={4}>
                    <Typography><b>Invoice: </b>#{data?.studentData?.invoiceNo || state?.invoiceNo}</Typography>
                  </Grid>
                  <Grid item xs={2}>
                    <Typography><b>Payment Mode</b></Typography>
                  </Grid>
                  <Grid item xs={2}>Invoice / Bill</Grid>
                  <Grid item xs={4}>
                    <Typography>
                      <b>Order Date :</b>{" "}
                      {(data?.studentData?.createdDate && fDate(data?.studentData?.createdDate)) || "-"}
                    </Typography>
                  </Grid>
                  <Grid item xs={4}></Grid>
                  <Grid item xs={2}>
                    <Typography>
                      {data?.studentData?.paymentMode
                        ? data.studentData.paymentMode.toUpperCase()
                        : "-"}
                    </Typography>
                  </Grid>
                </Grid>

                <Box
                  sx={{
                    mt: 1,
                    color: "black",
                    background: "rgb(192, 192, 192)",
                    padding: "10px",
                    display: "flex",
                    flexWrap: "wrap",
                  }}
                >
                  <Typography sx={{ width: { xs: "100%", md: "50%" } }}>
                    Shipping / Billing Details:
                  </Typography>
                  <Typography sx={{ width: { xs: "100%", md: "50%" } }}>
                    Student Details:
                  </Typography>
                </Box>

                <Grid container spacing={0} mb={2}>
                  <Grid item xs={12} md={6}>
                    <Typography>
                      <b>
                        {data?.studentData?.parentFirstName || ""}{" "}
                        {data?.studentData?.parentLastName || ""}
                      </b>
                    </Typography>
                    <Typography sx={{ whiteSpace: "pre-wrap", wordBreak: "break-word", maxWidth: "100%" }}>
                      {data?.studentData?.address1} {data?.studentData?.address2}{" "}
                      {data?.studentData?.city}, {data?.studentData?.stateName}{" "}
                      {data?.studentData?.pin}.
                    </Typography>
                    <Typography><b>Mobile No:</b> +91 {data?.studentData?.mobile}</Typography>
                  </Grid>
                  <Grid item xs={12} md={6}>
                    <Typography>
                      <b>{data?.studentData?.firstName} {data?.studentData?.lastName}</b>
                    </Typography>
                    <Typography noWrap><b>School:</b> {data?.studentData?.schoolName || "-"}</Typography>
                    <Typography><b>Grade:</b> {data?.studentData?.stdName || "-"}</Typography>
                    <Typography><b>Division:</b> {data?.studentData?.division || "-"}</Typography>
                    <Typography><b>Roll No:</b> {data?.studentData?.rollNo || "-"}</Typography>
                  </Grid>
                </Grid>

                <Typography
                  variant="h6"
                  sx={{
                    mb: 2,
                    background: "rgb(192, 192, 192)",
                    color: "black",
                    padding: "10px",
                    borderRadius: "4px",
                    textAlign: "center",
                  }}
                  className="orderText"
                >
                  Order Details: {data?.orderDetails[0]?.packageName || "NA"}
                </Typography>

                <Scrollbar>
                  <TableContainer sx={{ minWidth: "100%", overflowX: "auto" }}>
                    <Table sx={{ width: "100%", tableLayout: "auto", border: "2px solid black" }}>
                      <TableHead>
                        <TableRow>
                          <TableCell align="center" sx={headerStyle}>Sr. No.</TableCell>
                          <TableCell align="left" sx={headerStyle} width={300}>Items</TableCell>
                          <TableCell align="center" sx={headerStyle}>HSN Code</TableCell>
                          <TableCell align="center" sx={headerStyle}>Qty</TableCell>
                          <TableCell align="center" sx={headerStyle}>Total Price</TableCell>
                          {isMaharashtra ? (
                            <>
                              <TableCell align="center" sx={headerStyle}>CGST</TableCell>
                              <TableCell align="center" sx={headerStyle}>SGST</TableCell>
                            </>
                          ) : (
                            <TableCell align="center" sx={headerStyle}>IGST</TableCell>
                          )}
                          <TableCell align="center" sx={headerStyle}>Total</TableCell>
                        </TableRow>
                      </TableHead>

                      <TableBody>
                        {/* UI: continuous Sr. No. across all categories */}
                        {(() => {
                          let srNo = 0;
                          return Object.entries(invoiceData).map(
                            ([category, items], index) => (
                              <React.Fragment key={index}>
                                <TableRow>
                                  <TableCell colSpan={8} align="center" sx={{ border: "2px solid black" }}>
                                    <Typography variant="h5">
                                      {data?.orderDetails[0]?.packageName || "NA"} - {category}
                                    </Typography>
                                  </TableCell>
                                </TableRow>

                                {items.map((el, subIndex) => {
                                  srNo++;
                                  return (
                                    <TableRow key={`${index}-${subIndex}`}>
                                      <TableCell align="center" sx={cellStyle}>{srNo}</TableCell>
                                      <TableCell align="left" sx={cellStyle}>
                                        <Typography variant="subtitle2">{el.prodName}</Typography>
                                        <Typography variant="body2" sx={{ fontStyle: "italic" }}>
                                          {el.prodDesc && el.prodDesc.trim() !== "" && el.prodDesc !== "null"
                                            ? el.prodDesc
                                            : "No description available"}
                                        </Typography>
                                      </TableCell>
                                      <TableCell align="center" sx={cellStyle}>{el?.hsncode || "-"}</TableCell>
                                      <TableCell align="center" sx={cellStyle}>{el.qty}</TableCell>
                                      <TableCell align="center" sx={cellStyle}>₹ {el?.totalPriceBeforeTax || 0}</TableCell>
                                      {isMaharashtra ? (
                                        <>
                                          <TableCell align="center" sx={cellStyle}>{el.cgst}%<br />₹ {el?.cgstAmount || 0}</TableCell>
                                          <TableCell align="center" sx={cellStyle}>{el.sgst}%<br />₹ {el?.sgstAmount || 0}</TableCell>
                                        </>
                                      ) : (
                                        <TableCell align="center" sx={cellStyle}>{el.igst}%<br />₹ {el?.igstAmount || 0}</TableCell>
                                      )}
                                      <TableCell align="center" sx={{ ...cellStyle, minWidth: 120 }}>₹ {el?.amount || 0}</TableCell>
                                    </TableRow>
                                  );
                                })}
                              </React.Fragment>
                            )
                          );
                        })()}

                        <RowResultStyle>
                          <TableCell sx={cellStyle} colSpan={colSpanBeforeTaxColumn} />
                          <TableCell align="right" sx={cellStyle}><Typography>Subtotal:</Typography></TableCell>
                          <TableCell align="right" sx={cellStyle}>
                            ₹ {Number(data?.orderDetails?.reduce((acc, crr) => acc + crr.totalPriceBeforeTax, 0) || 0).toFixed(2)}
                          </TableCell>
                        </RowResultStyle>

                        <RowResultStyle>
                          <TableCell sx={cellStyle} colSpan={colSpanBeforeTaxColumn} />
                          <TableCell align="right" sx={cellStyle}><Typography>Shipping:</Typography></TableCell>
                          <TableCell align="right" sx={cellStyle}>
                            ₹ {Number(data?.studentData?.shippingPrice || 0).toFixed(2)}
                          </TableCell>
                        </RowResultStyle>

                        {Number(data?.studentData?.discountAmount || 0) > 0 && (
                          <RowResultStyle>
                            <TableCell sx={cellStyle} colSpan={colSpanBeforeTaxColumn} />
                            <TableCell align="right" sx={cellStyle}>
                              <Typography sx={{ color: "success.main" }}>Discount:</Typography>
                            </TableCell>
                            <TableCell align="right" sx={{ ...cellStyle, color: "green" }}>
                              -₹ {Number(data.studentData.discountAmount).toFixed(2)}
                            </TableCell>
                          </RowResultStyle>
                        )}

                        <RowResultStyle>
                          <TableCell sx={cellStyle} colSpan={colSpanBeforeTaxColumn} />
                          <TableCell align="right" sx={cellStyle}><Typography>Taxes:</Typography></TableCell>
                          <TableCell align="right" sx={cellStyle}>
                            ₹ {Number(data?.orderDetails?.reduce((acc, crr) => acc + (crr.amount - crr.totalPriceBeforeTax), 0) || 0).toFixed(2)}
                          </TableCell>
                        </RowResultStyle>

                        <RowResultStyle>
                          <TableCell sx={cellStyle} colSpan={colSpanBeforeTaxColumn} />
                          <TableCell align="right" sx={cellStyle}><Typography variant="h6">Total:</Typography></TableCell>
                          <TableCell align="right" sx={cellStyle}>
                            <Typography variant="h6">
                              ₹{" "}
                              {(
                                Number(data?.orderDetails?.reduce((acc, crr) => acc + crr.amount, 0) || 0) +
                                Number(data?.studentData?.shippingPrice || 0) -
                                Number(data?.studentData?.discountAmount || 0)
                              ).toFixed(2)}
                            </Typography>
                          </TableCell>
                        </RowResultStyle>
                      </TableBody>
                    </Table>
                  </TableContainer>
                </Scrollbar>

                <Divider sx={{ mt: 5 }} />
                <Grid container>
                  <Grid item xs={12} md={9} sx={{ py: 3 }}>
                    <Typography variant="subtitle2">Declaration</Typography>
                    <Typography variant="body2">
                      The goods sold are intended for end user consumption and not for resale.
                      <br />
                      This is a computer generated invoice.
                    </Typography>
                  </Grid>
                  <Grid item xs={12} md={3} sx={{ py: 3, textAlign: "right" }}>
                    <Typography variant="subtitle2">Order Through</Typography>
                  </Grid>
                </Grid>
              </Card>

              {/* ── HIDDEN PDF PAGES — each slab = separate invoice, Sr. No. from 1 ── */}
              <Box sx={{ position: "absolute", left: "-9999px", top: 0 }}>
                {(() => {
                  const withoutGSTItems = data?.orderDetailsWithoutGST || [];
                  const withGSTItems = data?.orderDetailsWithGST || [];
                  const pages = [];

                  // Without GST slab — Sr. No. starts from 1
                  if (withoutGSTItems.length > 0) {
                    pages.push(
                      ...renderPDFTable(
                        withoutGSTItems,
                        `Order Details: ${data?.orderDetails[0]?.packageName || "NA"}`
                      )
                    );
                  }

                  // With GST slabs — each rate = separate invoice, Sr. No. from 1
                  if (withGSTItems.length > 0) {
                    const gstGroups = {};
                    withGSTItems.forEach((item) => {
                      const gstRate = isMaharashtra
                        ? item.cgst + item.sgst
                        : item.igst;
                      if (!gstGroups[gstRate]) {
                        gstGroups[gstRate] = [];
                      }
                      gstGroups[gstRate].push(item);
                    });

                    const sortedGstRates = Object.keys(gstGroups).sort(
                      (a, b) => Number(a) - Number(b)
                    );

                    sortedGstRates.forEach((gstRate) => {
                      pages.push(
                        ...renderPDFTable(
                          gstGroups[gstRate],
                          `Order Details: ${data?.orderDetails[0]?.packageName || "NA"}`
                        )
                      );
                    });
                  }

                  return pages;
                })()}

                {renderShippingPDFPage()}
              </Box>
            </>
          )}
        </Container>
      </Page>
    </>
  );
}